import {useEffect, useRef, useState} from "react";
import "./cardModal.css";
import {LabelsModal} from "./Modals/Labels/LabelsModal";
import {useDispatch, useSelector} from "react-redux";
import {LabelsAPI} from "../../../../../../store/api/get/Labels/Labels";
import "react-quill-new/dist/quill.snow.css";
import {PostDescriptionAPI} from "../../../../../../store/api/get/Description/DescriptionAPI";
import {CardLabels} from "../../Cards/CardLabels";
import {ChecklistModal} from "./Modals/Checklist/ChecklistModal";
import {CheckLists} from "./CheckLists/CheckLists";
import DOMPurify from "dompurify";
import {API_URL, IMAGE_URL} from "../../../../../../URL";
import {
    DeleteAttachment,
    HandleCardMemberAPI,
    MakeCoverAPI,
    UploadAttachment
} from "../../../../../../store/api/get/Board/Board";
import {Comments} from "./Comments/Comments";
import {Editor} from "../../../../../../components/Editor/Editor";
import "react-datepicker/dist/react-datepicker.css";
import {DatesModal} from "./Dates/DatesModal";
import {Dates} from "./Dates/Dates";
import {MembersModal} from "./Modals/Members/MembersModal";
import {SET_ACTIVE_CARD, SET_BOARD_LISTS, SET_DESCRIPTION} from "../../../../../../store/constants";

let pasteLock = false;

const getMemberInitials = (member) => {
    const email = member?.email || "Member";
    return email.slice(0, 2).toUpperCase();
};

const getMemberAvatarUrl = (member) => {
    const avatar = member?.avatar || member?.image || member?.photo;
    if (!avatar) return null;
    if (
        avatar.startsWith("http://") ||
        avatar.startsWith("https://") ||
        avatar.startsWith("data:")
    ) {
        return avatar;
    }
    return IMAGE_URL + avatar;
};

export const CardModal = ({onClose}) => {
    const dispatch = useDispatch();

    const modalRef = useRef(null);
    const labelsBtnRef = useRef(null);
    const checklistBtnRef = useRef(null);
    const datesBtnRef = useRef(null);
    const membersBtnRef = useRef(null);
    const attachmentsRef = useRef([]);
    const titleInputRef = useRef(null);

    const card = useSelector((state) => state?.boards?.activeCard);
    const activeBoard = useSelector((state) => state?.boards?.activeBoard);
    const boardLists = useSelector((state) => state?.boards?.boardLists || []);
    const user = useSelector((state) => state?.user?.user);

    const [description, setDescription] = useState(null);
    const [focusEditor, setFocusEditor] = useState(false);
    const [localDescription, setLocalDescription] = useState(null);
    const [isEditDescription, setIsEditDescription] = useState(false);
    const [resizeWidths, setResizeWidths] = useState([]);
    const [isDescriptionEmpty, setIsDescriptionEmpty] = useState(true);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState("");
    const [isSavingTitle, setIsSavingTitle] = useState(false);
    const [isUpdatingCard, setIsUpdatingCard] = useState(false);
    const [previewAttachment, setPreviewAttachment] = useState(null);

    const [isLabelsOpen, setIsLabelsOpen] = useState(false);
    const [labelsPosition, setLabelsPosition] = useState({
        top: 0,
        left: 0,
    });

    const [isChecklistOpen, setIsChecklistOpen] = useState(false);
    const [checklistPosition, setChecklistPosition] = useState({
        top: 0,
        left: 0,
    });

    const [isDatesOpen, setIsDatesOpen] = useState(false);
    const [datesPosition, setDatesPosition] = useState({
        top: 0,
        left: 0,
    });
    const [startDateEnabled, setStartDateEnabled] = useState(true);
    const [dueDateEnabled, setDueDateEnabled] = useState(true);
    const [startDate, setStartDate] = useState(new Date());
    const [dueDate, setDueDate] = useState(new Date());

    const [isMembersOpen, setIsMembersOpen] = useState(false);
    const [membersPosition, setMembersPosition] = useState({
        top: 0,
        left: 0,
    });

    const boardMembers = activeBoard?.rbac || [];
    const selectedMembers = (card?.members || []).map((member) => {
        const boardMember = boardMembers.find(
            (item) => Number(item.user_id) === Number(member.user_id)
        );
        return {
            ...boardMember,
            ...member,
            email: member.email || boardMember?.email || "Unknown member",
        };
    });

    const userRole = boardMembers.find(
        (item) => Number(item.user_id) === Number(user?.id)
    )?.role;

    const currentDescription = localDescription || card?.description;
    const hasDescription = currentDescription &&
        !Array.isArray(currentDescription) &&
        Object.keys(currentDescription).length > 0 &&
        currentDescription?.text?.trim() &&
        currentDescription?.text?.trim() !== '<p><br></p>' &&
        currentDescription?.text?.trim() !== '<p></p>';

    const hasDate = card?.date &&
        !Array.isArray(card.date) &&
        Object.keys(card.date).length > 0;

    useEffect(() => {
        dispatch(LabelsAPI());
    }, [dispatch]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") {
                if (previewAttachment) {
                    setPreviewAttachment(null);
                    return;
                }
                if (isEditingTitle) {
                    setIsEditingTitle(false);
                    setTitleDraft(card?.name || "");
                    return;
                }
                onClose();
            }
        };
        document.addEventListener("keydown", handleEsc);
        return () => {
            document.removeEventListener("keydown", handleEsc);
        };
    }, [card?.name, isEditingTitle, onClose, previewAttachment]);

    useEffect(() => {
        setLocalDescription(card?.description || null);
        setDescription(null);
        setResizeWidths([]);
        setIsDescriptionEmpty(true);
        setIsEditingTitle(false);
        setTitleDraft(card?.name || "");
        setPreviewAttachment(null);

        if (!card?.description ||
            Array.isArray(card.description) ||
            Object.keys(card.description).length === 0 ||
            !card.description?.text ||
            card.description?.text?.trim() === '' ||
            card.description?.text?.trim() === '<p><br></p>' ||
            card.description?.text?.trim() === '<p></p>'
        ) {
            setIsEditDescription(true);
        } else {
            setIsEditDescription(false);
        }

        setIsMembersOpen(false);
        setIsLabelsOpen(false);
        setIsChecklistOpen(false);
        setIsDatesOpen(false);
    }, [card?.id]);

    useEffect(() => {
        if (isEditingTitle) {
            titleInputRef.current?.focus();
            titleInputRef.current?.select();
        }
    }, [isEditingTitle]);

    useEffect(() => {
        attachmentsRef.current = card?.attachments || [];
    }, [card?.attachments]);

    useEffect(() => {
        const handlePaste = async (e) => {
            if (!card?.id || pasteLock) return;

            const items = Array.from(e.clipboardData?.items || []);
            const imageItem = items.find((item) =>
                item.type.startsWith("image/")
            );

            if (!imageItem) return;

            const file = imageItem.getAsFile();
            if (!file) return;

            pasteLock = true;
            const hasAttachments = attachmentsRef.current.length > 0;
            const isFirstAttachment = !hasAttachments;
            const newImage = {
                id: Date.now() + Math.random(),
                name: file.name,
                file,
                added_at: "Added just now",
                is_cover: isFirstAttachment,
            };

            attachmentsRef.current = [
                ...attachmentsRef.current,
                newImage,
            ];

            try {
                await dispatch(
                    UploadAttachment(
                        card.id,
                        file,
                        newImage.name,
                        isFirstAttachment,
                        activeBoard?.id
                    )
                );
            } catch (error) {
                attachmentsRef.current = attachmentsRef.current.filter(
                    (attachment) => attachment.id !== newImage.id
                );
                alert(error.message || "Could not upload the attachment.");
            } finally {
                setTimeout(() => {
                    pasteLock = false;
                }, 1000);
            }
        };

        document.addEventListener("paste", handlePaste);
        return () => {
            document.removeEventListener("paste", handlePaste);
        };
    }, [card?.id, activeBoard?.id, dispatch]);

    const handleOverlayClick = (e) => {
        if (!focusEditor) {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose();
            }
        }
    };

    const closeActionModals = () => {
        setIsLabelsOpen(false);
        setIsChecklistOpen(false);
        setIsDatesOpen(false);
        setIsMembersOpen(false);
    };

    const handleStartTitleEdit = () => {
        setTitleDraft(card?.name || "");
        setIsEditingTitle(true);
    };

    const handleCancelTitleEdit = () => {
        setTitleDraft(card?.name || "");
        setIsEditingTitle(false);
    };

    const handleSaveTitle = async () => {
        const name = titleDraft.trim();

        if (!name || !card?.id || isSavingTitle) return;

        if (name === card.name) {
            setIsEditingTitle(false);
            return;
        }

        setIsSavingTitle(true);

        try {
            const response = await fetch(API_URL + "/post/rename_board_card/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({id: card.id, name}),
            });

            if (!response.ok) throw new Error("Could not rename card");

            const updatedCard = await response.json();
            dispatch({type: SET_ACTIVE_CARD, payload: updatedCard});
            dispatch({
                type: SET_BOARD_LISTS,
                payload: boardLists.map((list) => ({
                    ...list,
                    cards: (list.cards || []).map((listCard) =>
                        listCard.id === updatedCard.id
                            ? {...listCard, name: updatedCard.name}
                            : listCard
                    ),
                })),
            });
            setIsEditingTitle(false);
        } finally {
            setIsSavingTitle(false);
        }
    };

    const handleTitleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSaveTitle();
        }

        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            handleCancelTitleEdit();
        }
    };

    const handleArchiveCard = async () => {
        if (!card?.id || userRole === 1 || isUpdatingCard) return;

        setIsUpdatingCard(true);

        try {
            const response = await fetch(API_URL + "/post/handle_board_card/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({id: card.id}),
            });

            if (!response.ok) throw new Error("Could not archive card");

            const updatedCard = await response.json();
            dispatch({
                type: SET_BOARD_LISTS,
                payload: boardLists.map((list) => ({
                    ...list,
                    cards: (list.cards || []).map((listCard) =>
                        listCard.id === updatedCard.id
                            ? {...listCard, is_archive: updatedCard.is_archive}
                            : listCard
                    ),
                })),
            });
            onClose();
        } finally {
            setIsUpdatingCard(false);
        }
    };

    const handleDeleteCard = async () => {
        if (!card?.id || userRole === 1 || isUpdatingCard) return;

        setIsUpdatingCard(true);

        try {
            const response = await fetch(API_URL + "/delete/remove_board_card/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({id: card.id}),
            });

            if (!response.ok) throw new Error("Could not delete card");

            dispatch({
                type: SET_BOARD_LISTS,
                payload: boardLists.map((list) => ({
                    ...list,
                    cards: (list.cards || []).filter(
                        (listCard) => listCard.id !== card.id
                    ),
                })),
            });
            onClose();
        } finally {
            setIsUpdatingCard(false);
        }
    };

    const handleOpenLabels = (e) => {
        e.stopPropagation();
        if (!isLabelsOpen && labelsBtnRef.current) {
            const rect = labelsBtnRef.current.getBoundingClientRect();
            setLabelsPosition({
                top: rect.bottom + 8,
                left: rect.left,
            });
        }
        setIsChecklistOpen(false);
        setIsDatesOpen(false);
        setIsMembersOpen(false);
        setIsLabelsOpen((prev) => !prev);
    };

    const handleOpenChecklist = (e) => {
        e.stopPropagation();
        if (userRole === 1) return;
        if (!isChecklistOpen && checklistBtnRef.current) {
            const rect = checklistBtnRef.current.getBoundingClientRect();
            setChecklistPosition({
                top: rect.bottom + 8,
                left: rect.left,
            });
        }
        setIsLabelsOpen(false);
        setIsDatesOpen(false);
        setIsMembersOpen(false);
        setIsChecklistOpen((prev) => !prev);
    };

    const handleOpenDates = (e) => {
        e.stopPropagation();
        if (!isDatesOpen && datesBtnRef.current) {
            const rect = datesBtnRef.current.getBoundingClientRect();
            setDatesPosition({
                top: rect.bottom + 8,
                left: rect.left,
            });
        }
        setIsLabelsOpen(false);
        setIsChecklistOpen(false);
        setIsMembersOpen(false);
        setIsDatesOpen((prev) => !prev);
    };

    const handleOpenMembers = (e) => {
        e.stopPropagation();
        if (!isMembersOpen && membersBtnRef.current) {
            const rect = membersBtnRef.current.getBoundingClientRect();
            setMembersPosition({
                top: rect.bottom + 8,
                left: rect.left,
            });
        }
        setIsLabelsOpen(false);
        setIsChecklistOpen(false);
        setIsDatesOpen(false);
        setIsMembersOpen((prev) => !prev);
    };

    const handleToggleMember = async (member) => {
        const userId = Number(member.user_id);
        const cardId = Number(card?.id);
        if (!userId || !cardId) return;
        await dispatch(HandleCardMemberAPI(userId, cardId));
    };

    const handleDescription = (e, editor) => {
        const html = editor.getData();
        const editableElement = editor.ui.view.editable.element;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const figures = editableElement.querySelectorAll("figure.image");

        doc.querySelectorAll("img").forEach((img, index) => {
            const figure = figures[index];
            if (!figure) return;
            const editorWidth = editableElement.getBoundingClientRect().width || 1;
            const figureWidth = figure.getBoundingClientRect().width;
            const widthPercent = Math.round((figureWidth / editorWidth) * 100);
            img.setAttribute("data-resize-width", `${widthPercent}%`);
        });

        const cleanHtml = doc.body.innerHTML;
        const textContent = doc.body.textContent
            ?.replace(/\u00a0/g, " ")
            .trim();
        const isEmpty = !textContent && !doc.body.querySelector("img");

        setIsDescriptionEmpty(isEmpty);

        setDescription({
            card_id: card?.id,
            html: cleanHtml,
            resize_widths: [],
            is_edit: !!isEditDescription,
        });
    };

    const removeDescription = async () => {
        const response = await fetch(API_URL + "/post/remove_description/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                card_id: card?.id
            })
        });

        if (!response.ok) return;

        dispatch({
            type: SET_DESCRIPTION,
            payload: {
                card_id: card?.id,
                description: null,
            },
        });
        setLocalDescription(null);
        setDescription(null);
        setIsDescriptionEmpty(true);
    };

    const handleSaveDescription = async () => {
        // CKEditor only supplies a draft after its content changes. An untouched
        // editor (including after reopening the modal) must not delete saved text.
        if (!description) {
            setIsEditDescription(!hasDescription);
            return;
        }

        if (isDescriptionEmpty) {
            await removeDescription();
            return;
        }

        const payload = {
            ...description,
            resize_widths: resizeWidths,
        };

        const savedDescription = await dispatch(PostDescriptionAPI(payload));

        setLocalDescription({
            id: savedDescription?.description_id,
            text: savedDescription?.html,
            images: savedDescription?.images || [],
        });

        setIsEditDescription(false);
        setIsDescriptionEmpty(false);
    };

    const handleCancelEdit = () => {
        // A card without a saved description must keep its editor open. Otherwise
        // cancelling the first edit leaves no way to add a description again.
        setIsEditDescription(!hasDescription);
        setIsDescriptionEmpty(true);
        setDescription(null);
    };

    const getEditorData = () => {
        if (!currentDescription || Array.isArray(currentDescription)) {
            return "";
        }
        return currentDescription?.text || "";
    };

    const deleteAttachment = async (id) => {
        await dispatch(DeleteAttachment(id, activeBoard?.id));
    };

    const makeCover = async (attachmentId) => {
        const oldCover = card?.attachments?.find((item) => item.is_cover);
        await dispatch(
            MakeCoverAPI({
                cardId: card.id,
                oldId: oldCover?.id || null,
                newId: attachmentId,
                board_id: activeBoard?.id,
            })
        );
    };

    const coverAttachment = card?.attachments?.find((item) => item.is_cover) || card?.attachments?.[0];
    const coverUrl = coverAttachment?.url ? IMAGE_URL + coverAttachment.url : null;

    return (
        <div className="cardModalOverlay" onClick={handleOverlayClick}>
            <div
                className="cardModal"
                ref={modalRef}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="cardModal__topBar"
                    style={
                        coverUrl ? {
                            backgroundColor: "#1f1f22",
                            height: 240,
                        } : undefined
                    }
                >
                    <select
                        className="cardModal__dateSelect"
                        defaultValue="06.04.2026"
                    >
                        <option>06.04.2026</option>
                    </select>

                    {coverUrl && (
                        <div
                            className="cardModal__coverPreview cardModal__coverPreview--clickable"
                            onClick={() => setPreviewAttachment(coverAttachment)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setPreviewAttachment(coverAttachment);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="Open cover image preview"
                        >
                            <img
                                src={coverUrl}
                                alt={coverAttachment?.title || "Attachment preview"}
                                className="cardModal__coverImage"
                            />
                        </div>
                    )}

                    <div className="cardModal__topActions">
                        <button
                            className="cardModal__iconBtn"
                            onClick={onClose}
                            type="button"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div className="cardModal__body">
                    <div className="cardModal__main">
                        <div className="cardModal__titleRow">
                            {isEditingTitle ? (
                                <div className="cardModal__titleEditor">
                                    <input
                                        ref={titleInputRef}
                                        className="cardModal__titleInput"
                                        value={titleDraft}
                                        onChange={(e) => setTitleDraft(e.target.value)}
                                        onKeyDown={handleTitleKeyDown}
                                        aria-label="Card title"
                                        disabled={isSavingTitle}
                                    />
                                    <div className="cardModal__titleEditorActions">
                                        <button
                                            type="button"
                                            className="cardModal__titleSave"
                                            onClick={handleSaveTitle}
                                            disabled={isSavingTitle || isUpdatingCard || !titleDraft.trim()}
                                        >
                                            Save
                                        </button>
                                        <button
                                            type="button"
                                            className="cardModal__titleCancel"
                                            onClick={handleCancelTitleEdit}
                                            disabled={isSavingTitle || isUpdatingCard}
                                            aria-label="Cancel title edit"
                                        >
                                            ✕
                                        </button>
                                        {userRole !== 1 && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="cardModal__titleArchive"
                                                    onClick={handleArchiveCard}
                                                    disabled={isSavingTitle || isUpdatingCard}
                                                >
                                                    Archive
                                                </button>
                                                <button
                                                    type="button"
                                                    className="cardModal__titleDelete"
                                                    onClick={handleDeleteCard}
                                                    disabled={isSavingTitle || isUpdatingCard}
                                                >
                                                    Delete
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <h2
                                    className="cardModal__title"
                                    onClick={handleStartTitleEdit}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            handleStartTitleEdit();
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    title="Edit card title"
                                >
                                    {card?.name || "Board, List, Card - archive, delete functions"}
                                </h2>
                            )}
                        </div>

                        <div className="cardModal__actionsRow">
                            <button
                                className="cardModal__actionBtn"
                                type="button"
                            >
                                ＋ Add
                            </button>

                            <button
                                ref={labelsBtnRef}
                                className="cardModal__actionBtn"
                                onClick={handleOpenLabels}
                                type="button"
                            >
                                🏷 Labels
                            </button>

                            {!hasDate && (
                                <button
                                    ref={datesBtnRef}
                                    className="cardModal__actionBtn"
                                    onClick={handleOpenDates}
                                    type="button"
                                >
                                    🕘 Dates
                                </button>
                            )}

                            <button
                                ref={checklistBtnRef}
                                className="cardModal__actionBtn"
                                onClick={handleOpenChecklist}
                                type="button"
                            >
                                ☑ Checklist
                            </button>

                            <button
                                ref={membersBtnRef}
                                className="cardModal__actionBtn"
                                onClick={handleOpenMembers}
                                type="button"
                            >
                                👥 Members
                            </button>
                        </div>

                        {selectedMembers.length > 0 && (
                            <div className="cardModal__membersSelected">
                                <h3>Members:</h3>
                                <div className="cardModal__membersAvatars">
                                    {selectedMembers.map((member) => {
                                        const avatarUrl = getMemberAvatarUrl(member);
                                        return (
                                            <div
                                                className="cardModal__selectedMember"
                                                key={member.user_id}
                                                title={member.email}
                                            >
                                                {avatarUrl ? (
                                                    <img
                                                        src={avatarUrl}
                                                        alt={member.email}
                                                    />
                                                ) : (
                                                    <span>
                                                        {getMemberInitials(member)}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="cardModal__labels">
                            <h3>Labels:</h3>
                            <CardLabels card={card}/>
                        </div>

                        <Dates
                            card={card}
                            datesBtnRef={datesBtnRef}
                            handleOpenDates={handleOpenDates}
                        />

                        <div className="cardModal__section">
                            {card?.attachments?.length > 0 && (
                                <div className="cardModal__attachments">
                                    <div className="cardModal__sectionTitle cardModal__attachmentsHeader">
                                        <div>
                                            <span className="cardModal__sectionIcon">
                                                📎
                                            </span>
                                            <h3>Attachments</h3>
                                        </div>
                                    </div>

                                    <div className="cardModal__attachmentList">
                                        <h4>Files</h4>
                                        {card.attachments.map((item) => (
                                            <div
                                                className="cardModal__attachmentItem"
                                                key={item.id}
                                                onClick={() => item.url && setPreviewAttachment(item)}
                                                onKeyDown={(e) => {
                                                    if (item.url && (e.key === "Enter" || e.key === " ")) {
                                                        e.preventDefault();
                                                        setPreviewAttachment(item);
                                                    }
                                                }}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <img
                                                    src={IMAGE_URL + item.url}
                                                    alt={item.name}
                                                    className="cardModal__attachmentImage"
                                                />
                                                <div className="cardModal__attachmentInfo">
                                                    <strong>
                                                        {item.title}
                                                    </strong>
                                                    <p>
                                                        {item.created_at}
                                                        {item?.is_cover && " • 🖼 Cover"}
                                                    </p>
                                                </div>
                                                {!item?.is_cover && (
                                                    <button
                                                        type="button"
                                                        className="cardModal__attachmentMore"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            makeCover(item.id);
                                                        }}
                                                    >
                                                        Make Cover
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="cardModal__attachmentMore"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteAttachment(item.id);
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="cardModal__sectionTitle">
                                <span className="cardModal__sectionIcon">
                                    ☰
                                </span>
                                <h3>Description</h3>
                            </div>

                            {(!hasDescription || isEditDescription) && (
                                <>
                                    <Editor
                                        getEditorData={getEditorData}
                                        handleEditor={handleDescription}
                                        setFocusEditor={setFocusEditor}
                                        cardId={card?.id}
                                    />

                                    <div className="cardModal__editorFooter">
                                        <div className="cardModal__editorFooterLeft">
                                            <button
                                                className="cardModal__saveBtn"
                                                onClick={handleSaveDescription}
                                                type="button"
                                            >
                                                Save
                                            </button>

                                            <button
                                                className="cardModal__cancelBtn"
                                                onClick={handleCancelEdit}
                                                type="button"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>

                                </>
                            )}

                            {hasDescription && !isEditDescription && (
                                <div
                                    className="card__description"
                                    onClick={() => setIsEditDescription(true)}
                                >
                                    <div
                                        className="card__description-text"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                currentDescription?.text
                                            ),
                                        }}
                                    />
                                    <button className="button__red" onClick={removeDescription}>
                                        Remove
                                    </button>
                                </div>
                            )}

                            <CheckLists/>
                        </div>
                    </div>

                    <Comments card={card} focusEditor={focusEditor} setFocusEditor={setFocusEditor}/>
                </div>

                {isDatesOpen && (
                    <DatesModal
                        card={card}
                        onClose={() => setIsDatesOpen(false)}
                        position={datesPosition}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        dueDate={dueDate}
                        setDueDate={setDueDate}
                        startDateEnabled={startDateEnabled}
                        setStartDateEnabled={setStartDateEnabled}
                        dueDateEnabled={dueDateEnabled}
                        setDueDateEnabled={setDueDateEnabled}
                        setIsDatesOpen={setIsDatesOpen}
                    />
                )}

                {isLabelsOpen && (
                    <LabelsModal
                        onClose={() => setIsLabelsOpen(false)}
                        position={labelsPosition}
                        triggerRef={labelsBtnRef}
                    />
                )}

                {isChecklistOpen && (
                    <ChecklistModal
                        onClose={() => setIsChecklistOpen(false)}
                        position={checklistPosition}
                        card={card}
                        setIsChecklistOpen={setIsChecklistOpen}
                    />
                )}

                {isMembersOpen && (
                    <MembersModal
                        position={membersPosition}
                        boardMembers={boardMembers}
                        selectedMembers={selectedMembers}
                        onToggleMember={handleToggleMember}
                        onClose={() => setIsMembersOpen(false)}
                        buttonRef={membersBtnRef}
                        imageUrl={IMAGE_URL}
                    />
                )}
            </div>

            {previewAttachment?.url && (
                <div
                    className="cardModal__attachmentPreviewOverlay"
                    onClick={(e) => {
                        e.stopPropagation();
                        setPreviewAttachment(null);
                    }}
                    role="presentation"
                >
                    <div
                        className="cardModal__attachmentPreview"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label={previewAttachment.title || "Attachment preview"}
                    >
                        <button
                            type="button"
                            className="cardModal__attachmentPreviewClose"
                            onClick={() => setPreviewAttachment(null)}
                            aria-label="Close image preview"
                        >
                            ✕
                        </button>
                        <img
                            src={IMAGE_URL + previewAttachment.url}
                            alt={previewAttachment.title || "Attachment preview"}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
